import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { resendOtp, validateOtp } from "../api/Service";
import { useOtpState } from "./useOtpState";
import { useTimer } from "./useTimer";

export const useOtpLogin = () => {
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const authStateKey = queryParams.get("auth_state_key") || "";

  const STORAGE_KEY = `otp_resend_expiry_${authStateKey}`;
  const RESEND_COOLDOWN_DURATION = 60;

  const { otp, inputRefs, resetOtp, handleChange, handleKeyDown, handlePaste, otpValue, isComplete } = useOtpState(6);

  const { timeLeft, isExpired, start: startTimer, reset: resetTimer } = useTimer(RESEND_COOLDOWN_DURATION, STORAGE_KEY);

  useEffect(() => {
    if (!authStateKey) {
      navigate("/saml/login");
    }
  }, [authStateKey, navigate]);

  const handleContinue = async () => {
    setIsLoading(true);
    setError("");
    try {
      const response = await validateOtp(otpValue, authStateKey);

      const data = await response.json();
      const { message = "", ssoRedirectUri = "" } = data;
      if (!response.ok) {
        if (response?.status === 429) {
          setFailedAttempts(3);
        } else {
          setFailedAttempts((prev) => prev + 1);
        }
        const error: any = new Error(message || "An unexpected error occurred.");
        error.ssoRedirectUri = ssoRedirectUri;
        throw error;
      } else if (ssoRedirectUri) window.location.replace(ssoRedirectUri);
    } catch (err: any) {
      const { message, ssoRedirectUri } = err;
      resetOtp();
      setError(message || "An unexpected error occurred.");
      if (ssoRedirectUri) window.location.replace(ssoRedirectUri);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    setIsResending(true);
    setError("");
    try {
      const response = await resendOtp(authStateKey);
      const data = await response.json();
      const { message = "", ssoRedirectUri = "" } = data;
      if (!response.ok) {
        const error: any = new Error(message || "An unexpected error occurred.");
        error.ssoRedirectUri = ssoRedirectUri;
        throw error;
      } else {
        startTimer();
        resetOtp();
        setFailedAttempts(0);
        if (ssoRedirectUri) window.location.replace(ssoRedirectUri);
      }
    } catch (err: any) {
      const { message, ssoRedirectUri } = err;
      setError("Failed to resend OTP: " + (message || err.message || "An unexpected error occurred."));
      if (ssoRedirectUri) window.location.replace(ssoRedirectUri);
    } finally {
      setIsResending(false);
    }
  };

  const handleCancel = () => {
    resetTimer();
    setFailedAttempts(0);
    navigate(-1);
  };

  const isLockedOut = failedAttempts >= 3;

  return {
    otp,
    timer: timeLeft,
    canResend: isExpired,
    error,
    isLoading,
    isResending,
    inputRefs,
    isLockedOut,
    handleChange: (index: number, value: string) => {
      setError("");
      handleChange(index, value);
    },
    handleKeyDown,
    handlePaste: (e: React.ClipboardEvent) => {
      setError("");
      handlePaste(e);
    },
    handleContinue,
    handleResend,
    handleCancel,
    canContinue: isComplete && !isLoading && !isLockedOut,
  };
};
